/**
 * ToolsMenu.jsx — Tools top-level menu (hidden in Simple Mode).
 *
 * Items: token usage, chapter analysis, novel analysis, outline
 * generation, Read Manuscript → Fill Bible, Flow Pass polish,
 * Live Coach, and Find & Replace All (global character/place rename).
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Wand2, Replace, Sparkles, History, Megaphone, GraduationCap, Flame, Loader2, ClipboardList, BellOff, Bell, Lock, FileUp, BookOpen, BriefcaseBusiness, PanelTop, PanelTopClose, ChevronsUpDown, Check, User, Zap } from 'lucide-react';
import { STATUSBAR_LS_KEY, STATUSBAR_EVENT } from '@/components/chrome/StatusBar';
import { toast } from 'sonner';
import { usePW } from '@/lib/store';
import { Menu, MenuItem } from './shared';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { cn } from '@/lib/utils';
import axios from 'axios';
import FleschAnalyzerDialog from '@/components/panels/FleschAnalyzerDialog';
import { HeatTensionAdjustDrawer } from '@/components/panels/HeatTensionAdjustDrawer';
import KdpCompliancePanel from '@/components/panels/KdpCompliancePanel';
import ManuscriptPolishPanel from '@/components/panels/ManuscriptPolishPanel';
import { BulkSynopsisImportDialog } from '@/components/dialogs/BulkSynopsisImportDialog';
import KdpMetadataDialog from '@/components/dialogs/KdpMetadataDialog';
import { FillLightChaptersDialog } from '@/components/dialogs/FillLightChaptersDialog';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export function ToolsMenu({ onBuyTokens, visible, togglePanel, onOpenFlowPass, user }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { project, chapters, activeChapterId, characters } = usePW();
  const isAdmin = !!(user?.is_admin || user?.role === 'admin' || user?.role === 'super_admin');

  // ── Bulk Synopsis Import state ──────────────────────────────────────────
  const [bulkImportOpen, setBulkImportOpen] = useState(false);

  // ── Flesch Analyzer state ───────────────────────────────────────────────
  const [fleschOpen, setFleschOpen] = useState(false);

  // ── Heat & Tension Adjustment state ────────────────────────────────────
  const [htOpen, setHtOpen] = useState(false);

  // ── KDP Compliance state ─────────────────────────────────────────────────
  const [kdpOpen,     setKdpOpen]     = useState(false);
  const [kdpFormOpen, setKdpFormOpen] = useState(false);

  // ── Manuscript Polish state ──────────────────────────────────────────────
  const [polishOpen, setPolishOpen] = useState(false);

  // ── Fill Light Chapters state ────────────────────────────────────────────
  const [fillLightOpen, setFillLightOpen] = useState(false);
  // Listen for cross-component open request (e.g. right-click context menu)
  React.useEffect(() => {
    const onOpen = () => setFillLightOpen(true);
    window.addEventListener('loom:fill-light-open', onOpen);
    return () => window.removeEventListener('loom:fill-light-open', onOpen);
  }, []);

  // ── Find & Replace All state ────────────────────────────────────────────
  const [frOpen, setFrOpen] = useState(false);
  const [frOld, setFrOld] = useState('');
  const [frNew, setFrNew] = useState('');
  const [frLoading, setFrLoading] = useState(false);
  const [frCharPickerOpen, setFrCharPickerOpen] = useState(false);
  const [frPreview, setFrPreview] = useState(null);   // { total, chapters }
  const [frPreviewing, setFrPreviewing] = useState(false);
  const frPreviewTimer = React.useRef(null);

  const frFetchPreview = React.useCallback(async (name) => {
    if (!project?.id || !name.trim()) { setFrPreview(null); return; }
    setFrPreviewing(true);
    try {
      const r = await axios.post(
        `${API}/projects/${project.id}/find-replace-all/preview`,
        { old_name: name.trim() },
        { withCredentials: true },
      );
      setFrPreview(r.data);
    } catch { setFrPreview(null); }
    finally { setFrPreviewing(false); }
  }, [project?.id]);

  // Debounce: fetch preview 600 ms after the user stops typing
  React.useEffect(() => {
    clearTimeout(frPreviewTimer.current);
    if (!frOld.trim()) { setFrPreview(null); return; }
    frPreviewTimer.current = setTimeout(() => frFetchPreview(frOld), 600);
    return () => clearTimeout(frPreviewTimer.current);
  }, [frOld, frFetchPreview]);

  const runFindReplaceAll = async () => {
    if (!project?.id || !frOld.trim() || !frNew.trim() || frLoading) return;
    setFrLoading(true);
    try {
      const r = await axios.post(
        `${API}/projects/${project.id}/find-replace-all`,
        { old_name: frOld.trim(), new_name: frNew.trim() },
        { withCredentials: true, timeout: 60000 },
      );
      const d = r.data;
      if (d.replaced_count === 0 && d.characters_renamed === 0) {
        toast.info(`"${frOld}" not found in any chapter.`);
      } else {
        const parts = [];
        if (d.replaced_count > 0) parts.push(`${d.replaced_count} occurrence${d.replaced_count === 1 ? '' : 's'} across ${d.chapters_affected} chapter${d.chapters_affected === 1 ? '' : 's'}`);
        if (d.characters_renamed > 0) parts.push(`${d.characters_renamed} character record${d.characters_renamed === 1 ? '' : 's'} renamed`);
        toast.success(`Renamed "${frOld}" → "${frNew}": ${parts.join(' · ')}`);
        setFrOpen(false);
        setFrOld('');
        setFrNew('');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Find & Replace failed.');
    } finally {
      setFrLoading(false);
    }
  };

  const runChapterAnalysis = async () => {
    if (!project) return;
    const ch = chapters.find((c) => c.id === activeChapterId) || chapters[0];
    if (!ch) { toast.error(t('menu.noChapterLoaded')); return; }
    const { generateApi } = await import('@/lib/api');
    toast.promise(
      generateApi.analyzeChapter(ch.id),
      {
        loading: t('menu.analyzeChapterToast'),
        success: (data) => t('menu.analyzeChapterSuccess', {
          continuity: data.continuity ?? '?',
          pacing:     data.pacing     ?? '?',
        }),
        error: (e) => e?.response?.data?.detail || t('menu.analysisFailed'),
      },
    );
  };

  const runNovelAnalysis = async () => {
    if (!project) return;
    const { generateApi } = await import('@/lib/api');
    const promise = generateApi.analyzeNovel(project.id).then((data) => {
      window.dispatchEvent(new CustomEvent('cr-cache-status', {
        detail: { read: data.cache_read || 0, write: data.cache_write || 0 },
      }));
      return data;
    });
    toast.promise(
      promise,
      {
        loading: t('menu.analyzeNovelToast'),
        success: (data) => t('menu.analyzeNovelSuccess', {
          continuity: data.continuity ?? '?',
          plotholes:  data.plotholes  ?? 0,
        }),
        error: (e) => e?.response?.data?.detail || t('menu.analysisFailed'),
      },
    );
  };

  // ── Outline generation state ────────────────────────────────────────────────
  const [outlineBusy, setOutlineBusy] = useState(false);

  const runOutline = async () => {
    if (!project || outlineBusy) return;
    const { generateApi } = await import('@/lib/api');
    setOutlineBusy(true);
    try {
      await toast.promise(
        generateApi.outline({ project_id: project.id }),
        {
          loading: t('menu.generateOutlineToast'),
          success: () => t('menu.generateOutlineSuccess'),
          error:   (e) => e?.response?.data?.detail || t('menu.outlineFailed'),
        },
      );
    } finally {
      setOutlineBusy(false);
    }
  };

  const requestCoachSuggestion = () => {
    if (!activeChapterId && (!chapters || chapters.length === 0)) {
      toast.info('Open a chapter first to get a coach suggestion.');
      return;
    }
    // Fires the same event the editor's manual-coach trigger button fires
    window.dispatchEvent(new CustomEvent('loom:coach-trigger'));
  };

  // ── Info Bar (StatusBar) visibility ─────────────────────────────────────
  const [statusBarVisible, setStatusBarVisible] = useState(
    () => localStorage.getItem(STATUSBAR_LS_KEY) !== '0',
  );
  const toggleStatusBar = () => {
    const next = !statusBarVisible;
    setStatusBarVisible(next);
    localStorage.setItem(STATUSBAR_LS_KEY, next ? '1' : '0');
    window.dispatchEvent(new CustomEvent(STATUSBAR_EVENT));
    toast.success(next ? 'Info bar restored.' : 'Info bar hidden.');
  };

  // ── Quiet Mode (hide marketing/notice banners) ───────────────────────────
  const [bannersQuiet, setBannersQuiet] = useState(
    () => localStorage.getItem('loom_banners_quiet') !== '0',
  );
  const toggleQuietMode = () => {
    const next = !bannersQuiet;
    setBannersQuiet(next);
    localStorage.setItem('loom_banners_quiet', next ? '1' : '0');
    window.dispatchEvent(new CustomEvent('loom:banners-quiet'));
    toast.success(next ? 'Notices hidden — Loom is now quieter.' : 'Notices restored.');
  };

  const openCoachHistory = () => {
    window.dispatchEvent(new CustomEvent('loom:coach-history-open'));
  };

  return (
    <>
      <Menu label={t('menu.tools')} busy={outlineBusy}>
        <MenuItem onClick={onBuyTokens} tip="Check how many AI writing credits you've used — click to top up">{t('menu.tokenUsage')}</MenuItem>
        <MenuItem onClick={runChapterAnalysis} tip="AI-scan the open chapter for pacing, continuity, and craft issues">{t('menu.analyzeCurrentChapter')}</MenuItem>
        <MenuItem onClick={runNovelAnalysis} tip="Deep-read your entire manuscript for plot holes and story arcs">{t('menu.analyzeWholeNovel')}</MenuItem>
        <MenuItem onClick={runOutline} disabled={outlineBusy} data-testid="menu-tools-generate-outline"
          tip="Build a chapter-by-chapter outline from your existing draft">
          {outlineBusy
            ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Generating…</>
            : t('menu.generateOutline')
          }
        </MenuItem>
        <DropdownMenuSeparator />
        <MenuItem
          data-testid="menu-tools-read-manuscript"
          tip="Let the AI read your prose and automatically populate your Character Bible"
          onClick={() => {
            if (!visible.characters) togglePanel('characters');
            setTimeout(() => window.dispatchEvent(new Event('story-bible-autofill')), 700);
          }}
        >
          {t('menu.readManuscriptFillBible')}
        </MenuItem>
        <MenuItem
          data-testid="menu-tools-flow-pass"
          tip="Smooth out awkward sentences and tighten the narrative flow with one click"
          onClick={() => {
            if (!activeChapterId) { toast.info(t('menu.openChapterFirst')); return; }
            onOpenFlowPass();
          }}
        >
          <Wand2 className="h-3.5 w-3.5" /> {t('menu.polishReadability')}
        </MenuItem>
        <DropdownMenuSeparator />
        <MenuItem
          data-testid="menu-tools-coach-suggest"
          tip="Get a focused AI nudge for your very next story beat or scene"
          onClick={requestCoachSuggestion}
        >
          <Sparkles className="h-3.5 w-3.5 text-primary" /> Live Coach Suggestion
        </MenuItem>
        <MenuItem
          data-testid="menu-tools-coach-history"
          tip="Browse every coaching suggestion made so far on this project"
          onClick={openCoachHistory}
        >
          <History className="h-3.5 w-3.5" /> View Coach History
        </MenuItem>
        <DropdownMenuSeparator />
        <MenuItem
          data-testid="menu-tools-marketing-assets"
          tip="Create taglines, back-cover blurbs, and social-media posts for your novel"
          onClick={() => {
            if (!project) { toast.info('Open a project first.'); return; }
            window.dispatchEvent(new CustomEvent('loom:open-marketing-assets'));
          }}
        >
          <Megaphone className="h-3.5 w-3.5 text-primary" /> Generate Marketing Assets
        </MenuItem>
        <MenuItem
          data-testid="menu-tools-business-dashboard"
          tip="See sales trends, competitor data, and audience insights for your genre"
          onClick={() => navigate('/app/business')}
        >
          <BriefcaseBusiness className="h-3.5 w-3.5 text-emerald-600" /> Marketing &amp; Business Dashboard
        </MenuItem>
        <DropdownMenuSeparator />
        <MenuItem
          data-testid="menu-tools-find-replace-all"
          tip="Swap a character or place name everywhere in your manuscript in one step"
          onClick={() => {
            if (!project) { toast.info('Open a project first.'); return; }
            setFrOpen(true);
          }}
        >
          <Replace className="h-3.5 w-3.5" /> Rename character / place across manuscript
        </MenuItem>
        <MenuItem
          data-testid="menu-tools-fill-light"
          tip="Bulk-expand chapters that fall below their target word count using AI"
          onClick={() => {
            if (!project) { toast.info('Open a project first.'); return; }
            setFillLightOpen(true);
          }}
        >
          <Zap className="h-3.5 w-3.5 text-amber-500" /> Fill Light Chapters
        </MenuItem>
        <DropdownMenuSeparator />
        <MenuItem
          data-testid="menu-tools-flesch-analyzer"
          tip="Check how easy your writing is to read for your target audience"
          onClick={() => {
            if (!project) { toast.info('Open a project first.'); return; }
            setFleschOpen(true);
          }}
        >
          <GraduationCap className="h-3.5 w-3.5 text-primary" /> Flesch Reading Ease Analyzer
        </MenuItem>
        <MenuItem
          data-testid="menu-tools-heat-tension"
          tip="Dial the dramatic intensity of a passage up or down"
          onClick={() => {
            if (!project) { toast.info('Open a project first.'); return; }
            setHtOpen(true);
          }}
        >
          <Flame className="h-3.5 w-3.5 text-rose-500" /> Heat &amp; Tension Adjustment
        </MenuItem>
        <DropdownMenuSeparator />
        <MenuItem
          data-testid="menu-tools-manuscript-polish"
          tip="Run a full AI editing pass across every chapter in one go"
          onClick={() => {
            if (!project) { toast.info('Open a project first.'); return; }
            setPolishOpen(true);
          }}
        >
          <Sparkles className="h-3.5 w-3.5 text-violet-500" /> Whole-Manuscript Polish
        </MenuItem>
        <MenuItem
          data-testid="menu-tools-kdp-compliance"
          tip="Verify your manuscript meets Amazon's KDP publishing requirements"
          onClick={() => {
            if (!project) { toast.info('Open a project first.'); return; }
            setKdpOpen(true);
          }}
        >
          <ClipboardList className="h-3.5 w-3.5 text-primary" /> KDP Compliance Checker
        </MenuItem>
        <MenuItem
          data-testid="menu-tools-kdp-form"
          tip="Fill in your title, categories, and keywords for Amazon listing"
          onClick={() => {
            if (!project) { toast.info('Open a project first.'); return; }
            setKdpFormOpen(true);
          }}
        >
          <BookOpen className="h-3.5 w-3.5 text-primary" /> KDP Metadata Form
        </MenuItem>
        <DropdownMenuSeparator />
        <MenuItem
          data-testid="menu-tools-quiet-mode"
          onClick={toggleQuietMode}
        >
          {bannersQuiet
            ? <><Bell    className="h-3.5 w-3.5 text-muted-foreground" /> Show notices</>
            : <><BellOff className="h-3.5 w-3.5 text-muted-foreground" /> Hide notices</>
          }
        </MenuItem>
        <MenuItem
          data-testid="menu-tools-statusbar-toggle"
          onClick={toggleStatusBar}
        >
          {statusBarVisible
            ? <><PanelTopClose className="h-3.5 w-3.5 text-muted-foreground" /> Hide info bar</>
            : <><PanelTop      className="h-3.5 w-3.5 text-muted-foreground" /> Show info bar</>
          }
        </MenuItem>

        {/* ── Admin-only: Bulk Synopsis Importer ──────────────────── */}
        <DropdownMenuSeparator />
        {isAdmin ? (
          <MenuItem
            data-testid="menu-tools-bulk-synopsis-import"
            onClick={() => setBulkImportOpen(true)}
          >
            <FileUp className="h-3.5 w-3.5 text-amber-500" />
            Bulk Synopsis Import
          </MenuItem>
        ) : (
          <MenuItem
            data-testid="menu-tools-bulk-synopsis-import-locked"
            disabled
            className="opacity-40 cursor-not-allowed select-none"
            title="Admin access required"
          >
            <Lock className="h-3.5 w-3.5 text-muted-foreground" />
            Bulk Synopsis Import
          </MenuItem>
        )}
      </Menu>

      {/* Find & Replace All Dialog */}
      <Dialog open={frOpen} onOpenChange={(v) => { if (!frLoading) { setFrOpen(v); if (!v) { setFrOld(''); setFrNew(''); setFrCharPickerOpen(false); } } }}>        <DialogContent className="max-w-md" data-testid="find-replace-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Replace className="h-4 w-4 text-primary" />
              Rename across manuscript
            </DialogTitle>
            <DialogDescription className="text-[12px]">
              Replaces every occurrence of a character or place name across all chapter content — and renames the matching character record if one exists.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            {/* ── Character picker ─────────────────────────────────── */}
            {(characters || []).length > 0 && (
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  Pick a character
                </Label>
                <Popover open={frCharPickerOpen} onOpenChange={setFrCharPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full justify-between h-8 text-[13px]"
                      data-testid="find-replace-char-picker-btn"
                    >
                      {frOld ? (
                        <span className="flex items-center gap-2 truncate">
                          <User className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                          <span className="truncate">{frOld}</span>
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Select a character…</span>
                      )}
                      <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[340px] p-0" align="start">
                    <Command className="min-h-0">
                      <CommandInput placeholder="Search characters…" data-testid="find-replace-char-search" />
                      <CommandList className="max-h-[240px]">
                        <CommandEmpty>No characters found.</CommandEmpty>
                        <CommandGroup>
                          {(characters || []).map((c) => (
                            <CommandItem
                              key={c.id}
                              value={[c.name, c.role].filter(Boolean).join(' ').trim() || c.id}
                              onSelect={() => {
                                setFrOld(c.name || '');
                                setFrCharPickerOpen(false);
                              }}
                              data-testid={`find-replace-char-option-${c.id}`}
                              className="flex items-center gap-2"
                            >
                              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/15 text-[11px] font-bold text-primary">
                                {(c.name || '?').split(' ').map((n) => n[0]).slice(0, 2).join('')}
                              </span>
                              <span className="flex-1 text-[13px]">{c.name}</span>
                              {c.role && (
                                <span className="shrink-0 text-[11px] text-muted-foreground">{c.role}</span>
                              )}
                              {frOld === c.name && (
                                <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
                              )}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                <p className="text-[10.5px] text-muted-foreground">
                  Or type any name below (place, thing, etc.):
                </p>
              </div>
            )}
            {/* ── Old name (manual) ────────────────────────────────── */}
            <div className="space-y-1">
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
                {(characters || []).length > 0 ? 'Old name' : 'Old name'}
              </Label>
              <Input
                value={frOld}
                onChange={(e) => setFrOld(e.target.value)}
                placeholder="e.g. Elara"
                maxLength={200}
                data-testid="find-replace-old-input"
                className="h-8 text-[13px]"
                autoFocus={(characters || []).length === 0}
              />
            </div>

            {/* ── Occurrence preview ─────────────────────────────────── */}
            {(frPreview || frPreviewing) && frOld.trim() && (
              <div
                className="rounded-md border border-border/60 bg-muted/40 px-3 py-2 text-[12px]"
                data-testid="find-replace-preview"
              >
                {frPreviewing ? (
                  <span className="flex items-center gap-1.5 text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" /> Counting occurrences…
                  </span>
                ) : frPreview?.total === 0 ? (
                  <span className="text-amber-600 dark:text-amber-400">
                    No occurrences of &ldquo;{frOld}&rdquo; found in any chapter.
                  </span>
                ) : (
                  <>
                    <p className="font-medium text-foreground">
                      <span className="text-primary">{frPreview.total}</span> occurrence{frPreview.total !== 1 ? 's' : ''} across {frPreview.chapters.length} chapter{frPreview.chapters.length !== 1 ? 's' : ''}
                    </p>
                    <ul className="mt-1.5 space-y-0.5">
                      {frPreview.chapters.map((c) => (
                        <li key={c.id} className="flex items-center gap-2 text-[11.5px] text-muted-foreground">
                          <span className="w-12 shrink-0 font-semibold text-foreground">Ch {c.number}</span>
                          <span className="flex-1 truncate">{c.title || 'Untitled'}</span>
                          <span className="shrink-0 rounded-full bg-primary/15 px-1.5 py-0.5 text-[10.5px] font-bold text-primary">{c.count}×</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}

            <div className="space-y-1">
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">New name</Label>
              <Input
                value={frNew}
                onChange={(e) => setFrNew(e.target.value)}
                placeholder="e.g. Lyra"
                maxLength={200}
                data-testid="find-replace-new-input"
                className="h-8 text-[13px]"
                onKeyDown={(e) => { if (e.key === 'Enter') runFindReplaceAll(); }}
              />
            </div>
            <p className="text-[10.5px] text-muted-foreground">
              Case-insensitive · affects all {chapters.length} chapter{chapters.length === 1 ? '' : 's'} in &ldquo;{project?.title || project?.name || 'this manuscript'}&rdquo;
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setFrOpen(false)} disabled={frLoading}>
              Cancel
            </Button>
            <Button
              onClick={runFindReplaceAll}
              disabled={frLoading || !frOld.trim() || !frNew.trim()}
              data-testid="find-replace-confirm-btn"
            >
              {frLoading ? 'Renaming…' : 'Rename everywhere'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Flesch Reading Ease Analyzer */}
      {fleschOpen && project?.id && (
        <FleschAnalyzerDialog
          open={fleschOpen}
          onClose={() => setFleschOpen(false)}
          projectId={project.id}
          activeChapterId={activeChapterId}
          chapters={chapters || []}
        />
      )}

      {/* Heat & Tension Adjustment */}
      {htOpen && project?.id && (
        <HeatTensionAdjustDrawer
          open={htOpen}
          onClose={() => setHtOpen(false)}
          projectId={project.id}
          activeChapterId={activeChapterId}
          chapters={chapters || []}
        />
      )}

      {/* KDP Compliance Checker */}
      {kdpOpen && project?.id && (
        <KdpCompliancePanel
          open={kdpOpen}
          onClose={() => setKdpOpen(false)}
          projectId={project.id}
        />
      )}

      {/* KDP Metadata Form */}
      {project?.id && (
        <KdpMetadataDialog
          open={kdpFormOpen}
          onOpenChange={setKdpFormOpen}
          projectId={project.id}
        />
      )}

      {/* Whole-Manuscript Polish */}
      {polishOpen && project?.id && (
        <ManuscriptPolishPanel
          open={polishOpen}
          onClose={() => setPolishOpen(false)}
          projectId={project.id}
          chapters={chapters || []}
        />
      )}

      {/* ── Admin: Bulk Synopsis Import ─────────────────────────── */}
      <BulkSynopsisImportDialog
        open={bulkImportOpen}
        onClose={() => setBulkImportOpen(false)}
      />

      {/* ── Fill Light Chapters wizard ────────────────────────────── */}
      <FillLightChaptersDialog
        open={fillLightOpen}
        onOpenChange={setFillLightOpen}
      />
    </>
  );
}
